import React, { useState } from "react";
import PropTypes from "prop-types";

const badgeClassByPriority = {
  Alta: "bg-danger",
  "Média": "bg-warning text-dark",
  Baixa: "bg-info text-dark",
};

const getConvocacaoTooltip = (row) => {
  if (row.convocacaoPermitida !== false) return "Convocar paciente";

  return row.anoPlanejado && row.mesPlanejado
    ? `A convocação está prevista apenas para ${row.mesPlanejado}/${row.anoPlanejado}.`
    : "A convocação não está disponível no período atual.";
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("pt-BR");
};

const ConvocacaoTable = ({
  title,
  subtitle,
  headerAction,
  rows,
  loading,
  total,
  selectedIds,
  allPageSelected,
  onTogglePage,
  onToggleRow,
  showPrioridade,
  onConvocarRow,
  onViewHistory,
  selectedCount,
  onClearSelection,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [statusSortAscending, setStatusSortAscending] = useState(true);
  const sortedRows = [...rows].sort((firstRow, secondRow) => {
    const firstStatus = firstRow.convocada ? 1 : 0;
    const secondStatus = secondRow.convocada ? 1 : 0;
    const comparison = firstStatus - secondStatus;

    return statusSortAscending ? comparison : -comparison;
  });

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div>
          <h6 className="mb-0">{title}</h6>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
        <div className="d-flex align-items-center gap-2">
          {headerAction}
          <span className="badge bg-primary">{total} resultados</span>
        </div>
      </div>

      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle convocacao-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    disabled={!rows.some((row) => row.convocacaoPermitida !== false)}
                    onChange={onTogglePage}
                  />
                </th>
                <th>Nome</th>
                <th>Exame</th>
                <th>Último Exame</th>
                <th>Prazo recomendado</th>
                {showPrioridade && <th>Prioridade</th>}
                <th>UBS</th>
                <th>
                  <button
                    className="border-0 bg-transparent p-0"
                    type="button"
                    style={{ color: "inherit", font: "inherit" }}
                    onClick={() => setStatusSortAscending((previous) => !previous)}
                  >
                    Situação <i className={`feather-${statusSortAscending ? "arrow-up" : "arrow-down"} ms-1`} />
                  </button>
                </th>
                <th style={{ width: 180 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={showPrioridade ? 10 : 9} className="text-center py-4">
                    Carregando pacientes...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={showPrioridade ? 10 : 9} className="text-center py-4 text-muted">
                    Nenhum paciente encontrado para os filtros selecionados.
                  </td>
                </tr>
              ) : (
                sortedRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        disabled={row.convocacaoPermitida === false}
                        onChange={() => onToggleRow(row.id)}
                      />
                    </td>
                    <td>
                      <div className="fw-semibold">{row.nome}</div>
                      <div className="small text-muted">CPF: {row.cpf}</div>
                      <div className="small text-muted">{row.idade} anos • Tel: {row.telefone || "-"}</div>
                      <div className="small text-muted">Endereço: {row.endereco || "-"}</div>
                    </td>
                    <td><span className="text-primary small fw-semibold">{row.exame}</span></td>
                    <td>
                      <div>{formatDate(row.dataReferencia)}</div>
                    </td>
                    <td>
                      <div>{formatDate(row.prazoRecomendado)}</div>
                      <div className="small text-danger">{row.statusGeralRastreamento || "-"}</div>
                    </td>
                    {showPrioridade && (
                      <td>
                        <span className={`badge ${badgeClassByPriority[row.prioridade] || "bg-secondary"}`}>
                          {row.prioridade}
                        </span>
                      </td>
                    )}
                    <td>{row.ubs}</td>
                    <td>
                      <span className={`badge ${row.convocada ? "bg-success" : "bg-warning text-dark"}`}>
                        {row.convocada ? "Convocada" : "Pendente"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-2">
                        <button
                          className="btn btn-outline-primary btn-sm convocacao-action-btn"
                          type="button"
                          onClick={() => onViewHistory(row)}
                        >
                          Ver histórico
                        </button>
                        <span
                          title={getConvocacaoTooltip(row)}
                          aria-label={getConvocacaoTooltip(row)}
                          className={row.convocacaoPermitida === false ? "d-inline-block" : ""}
                        >
                          <button
                            className="btn btn-primary btn-sm convocacao-action-btn"
                            type="button"
                            onClick={() => onConvocarRow(row.id)}
                            disabled={row.convocada || row.convocacaoPermitida === false}
                          >
                            {row.convocada ? "Convocada" : "Convocar"}
                          </button>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mt-3 convocacao-table-footer">
          <div className="d-flex align-items-center gap-3">
            <span className="small text-muted">{selectedCount} selecionados</span>
            <button className="btn btn-outline-secondary btn-sm" type="button" onClick={onClearSelection}>
              Limpar seleção
            </button>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted">Itens por página</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 84 }}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {[10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span className="small text-muted">
              {total === 0 ? "0-0" : `${page * pageSize + 1}-${Math.min(total, (page + 1) * pageSize)}`} de {total}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 0}
              type="button"
            >
              Anterior
            </button>
            <span className="small text-muted">
              Página {page + 1} de {totalPages}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page + 1 >= totalPages}
              type="button"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ConvocacaoTable.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  headerAction: PropTypes.node,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  total: PropTypes.number.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  allPageSelected: PropTypes.bool.isRequired,
  onTogglePage: PropTypes.func.isRequired,
  onToggleRow: PropTypes.func.isRequired,
  showPrioridade: PropTypes.bool,
  onConvocarRow: PropTypes.func,
  onViewHistory: PropTypes.func,
  selectedCount: PropTypes.number,
  onClearSelection: PropTypes.func,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
};

ConvocacaoTable.defaultProps = {
  title: "Pacientes elegíveis",
  subtitle: "",
  headerAction: null,
  showPrioridade: false,
  onConvocarRow: () => {},
  onViewHistory: () => {},
  selectedCount: 0,
  onClearSelection: () => {},
};

export default ConvocacaoTable;
